import { ShadowView, textStyles } from '../style/style'
import { StackActions, useNavigation } from '@react-navigation/native'
import { navigate } from '../root-navigation'
import React, { useState, useContext, useRef, useMemo, useEffect } from 'react'
import {
  ScrollView,
  StyleSheet,
  Text,
  Animated,
  View,
  TouchableOpacity,
  Platform,
  Alert,
} from 'react-native'
import Loader from '../utility/loader'
import api from '../api-client'
import Constants from 'expo-constants'
import * as Clipboard from 'expo-clipboard'
import { log } from '../utility'
import { version } from '../utility/updates'
import { ThemeContext } from '../utility/context/ThemeContext'
import { useAuthContext } from '../utility/context'
import { useAppDispatch, useAppSelector } from '../store'
import { CustomActionSheet, CustomActionSheetRef } from '../utility/CustomActionSheet'
import { ThemedButton } from '../utility/input/buttons'
import { MAX_CONTENT_WIDTH } from '../config'
import ThemedDialog from '../style/dialog'
import { FontAwesome6, Ionicons } from '@expo/vector-icons'
import SpecifyValues from '../specify-values'
import AppFeedbackForm from '../utility/forms/app-feedback'
import PermissionToggle from '../utility/permission-toggle'
import { User } from '@ecountabl/lib'
import Haptics from '../utility/haptics'
import Badge from '../utility/badge'
import Analytics from '../utility/analytics'
import { setUserField } from '../auth-slice'
import ThemeSelector from '../utility/theme-selector'
import * as ExpoApplication from 'expo-application'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import * as Sentry from '../utility/sentry'

enum PasswordResetState {
  IDLE = 'IDLE',
  CONFIRMING = 'CONFIRMING',
  SENDING = 'SENDING',
  SENT = 'SENT',
  CONFIRMED = 'CONFIRMED',
  ERROR = 'ERROR',
}

const Settings = (): JSX.Element => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { theme } = useContext(ThemeContext)
  const { primary, white, bgMain, danger, textMain, success } = theme.colors

  const { logout, user: authUser } = useAuthContext()
  const { user, deviceId } = useAppSelector((state) => state.auth)

  const userSettingInfo = useMemo(() => {

    if (!user || !authUser) {
      return null
    }

    if ((user as any)?.source === 'banno') {
      const displayName = `${user.firstName}${user.lastName ? ` ${user.lastName}` : ""}`
      const activeSince = new Date(user?.signupTimestamp).toDateString()
      return {
        userType: 'banno',
        displayName,
        email: user.email,
        activeSince,
      }
    }

    const subPrefix = authUser?.sub.split('|')[0]
    let userType = 'other'
    switch (subPrefix) {
      case 'auth0':
        userType = 'auth0'
        break
      case 'google-oauth2':
        userType = 'google'
        break
      case 'apple':
        userType = 'apple'
        break
    }

    const displayName = `${user.firstName}${user.lastName ? ` ${user.lastName}` : ""}`
    
    // check if email is hidden
    const isHiddenEmail = (email: string) => {
      if (!email || userType !== 'apple') return false

      // if apple we can check if the email is hidden by checking the domain
      const domain = email.split('@')[1]?.toLowerCase()
      return domain === 'privaterelay.appleid.com'
    }

    const email = (() => {
      switch (userType) {
        case 'apple':
          return isHiddenEmail(authUser?.email) ? 'Signed in with Apple ID' : authUser?.email
        default:
          return authUser?.email
      }
    })()

    const activeSince = new Date(user?.signupTimestamp).toDateString()

    return {
      userType,
      displayName,
      email,
      activeSince,
    }
  }, [authUser, user])

  const { institutions } = useAppSelector((state) => state.accountsSlice)

  const specifyValuesSheetRef = useRef<CustomActionSheetRef>(null)
  const feedbackSheetRef = useRef<CustomActionSheetRef>(null)
  const [deletingAccount, setDeletingAccount] = useState(false)
  const [passwordResetState, setPasswordResetState] = useState(PasswordResetState.IDLE)
  
  const clearPushTokenForLogout = async (): Promise<void> => {
    if (!deviceId || !user?.appPermissions?.[deviceId]?.notifications) {
      return
    }

    const currentStatus = user.appPermissions[deviceId].notifications.status || 'undetermined'
    await api.updateAppPermissions(deviceId, {
      notifications: {
        status: currentStatus,
        pushToken: '',
      }
    })
  }

  const performLogoutFlow = async (): Promise<void> => {
    setLoading(true)
    try {
      try {
        await clearPushTokenForLogout()
      } catch (error) {
        log.error('error updating app permissions on logout', { tags: { message: error instanceof Error ? error.message : 'Unknown error occurred.' } })
      }

      await logout()
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmDeleteAccount = (): void => {
    Analytics.track('delete_account_confirmed')
    setDeletingAccount(false)
    navigate('DeleteAccount')
  }

  const handleTriggerPasswordReset = async () => {
    setPasswordResetState(PasswordResetState.SENDING)
    try {
      await api.triggerPasswordReset()
      setPasswordResetState(PasswordResetState.SENT)
    } catch (error) {
      setError(error)
      setPasswordResetState(PasswordResetState.ERROR)
    }
  }

  const institutionsNeedingRelink = useMemo(() => {
    return institutions.filter((institution) => institution.error)
  }, [institutions])

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: bgMain }}>
        <Loader size="large" />
      </View>
    )
  }

  return <>
    <ScrollView
      style={{flex: 1, backgroundColor: bgMain}}
      contentContainerStyle={{flexGrow: 1, flexDirection: "column", justifyContent: "space-between", alignSelf: "center", maxWidth: MAX_CONTENT_WIDTH, width: "100%", gap: 30, paddingBottom: 10}}
      bounces={false}
    >
      <View style={{gap: 20}}>
        <ShadowView
          style={[
            {
              width: "100%",
              height: 100,
              borderBottomLeftRadius: 55,
              borderBottomRightRadius: 55,
              backgroundColor: primary,
              justifyContent: "center",
              alignItems: "center"
            }
          ]}
        >
          <Text style={[textStyles.h3, {color: white}]}>
            {userSettingInfo?.displayName}
          </Text>
          <Text style={[textStyles.label, {color: white}]}>
            {userSettingInfo?.email}
          </Text>
          <Text style={[textStyles.label, {color: white}]}>
            Active since: {userSettingInfo?.activeSince}
          </Text>
        </ShadowView>

        <View style={{flex: 1, paddingHorizontal: 16, gap: 20}}>
          <SettingsSection
            title='Profile'
            data={[
              {
                icon: <FontAwesome6 name="user-pen" size={20} color={textMain} />,
                text: "Manage Demographics",
                screen: 'ManageDemographics',
              },
              {
                icon: <FontAwesome6 name="list" size={20} color={textMain} />,
                text: "Modify Selected Values",
                onPress: () => specifyValuesSheetRef.current?.show(),
              },
              // {
              //   icon: <FontAwesome6 name="play" size={20} color={textMain} />,
              //   text: "Revisit Onboarding",
              //   onPress: () => Alert.alert("Onboarding", "Onboarding rerun is not available yet")
              // }
            ]}
          />

          <SettingsSection
            title='Accounts'
            data={[
              {
                icon: <FontAwesome6 name="credit-card" size={20} color={textMain} />,
                text: "Manage Accounts",
                screen: 'ManageAccounts',
                component: institutionsNeedingRelink.length ? (
                  <Badge
                    icon={
                      <Text style={{color: white}}>
                        {institutionsNeedingRelink.length}
                      </Text>
                    }
                    style={{backgroundColor: danger}}
                  />
                ) : null
              }
            ]}
          />
          {
            userSettingInfo?.userType === 'auth0' ? 
            <SettingsSection
              title='Security'
              data={[
                {
                  icon: <FontAwesome6 name="shield" size={20} color={textMain} />,
                  text: "Manage MFA Settings",
                  screen: 'ManageMfa',
                },
                {
                  icon: <FontAwesome6 name="key" size={20} color={textMain} />,
                  text: "Reset Password",
                  onPress: () => {
                    Analytics.track('reset_password_pressed')
                    setPasswordResetState(PasswordResetState.CONFIRMING)
                  },
                  disabled: false,
                  warningHaptic: true
                  }
              ]}
            /> : null
          }

          {
            // with only notification permissions, only show permnissions on native platforms
            Platform.OS !== 'web' ? (
              <SettingsSection
                title='Permissions'
                data={[
                  // {
                  //   icon: <FontAwesome6 name="location-dot" size={20} color={textMain} />,
                  //   text: "Location Access",
                  //   onPress: undefined,
                  //   component: (
                  //     <PermissionToggle
                  //       permission='location'
                  //     />
                  //   )
                  // },
                  {
                    icon: <Ionicons name="notifications" size={20} color={textMain} />,
                    text: "Notifications",
                    onPress: undefined,
                    component: (
                      <PermissionToggle
                        permission='notifications'
                      />
                    )
                  }
                ]}
              />
            ) : null
          }

          <View style={{ gap: 10 }}>
            <Text style={[textStyles.h2, { color: textMain }]}>Theme</Text>
            <ThemeSelector />
          </View>

          <SettingsSection
            title='Actions'
            data={[
              {
                icon: <Ionicons name="chatbox" size={20} color={textMain}/>,
                text: "Provide Feedback",
                onPress: () => {
                  feedbackSheetRef.current?.show()
                  Analytics.track('feedback_pressed')
                }
              },
              {
                icon: <FontAwesome6 name="door-open" size={20} color={textMain}/>,
                text: "Log Out",
                onPress: async () => {
                  Analytics.track('log_out_pressed')
                  try {
                    await performLogoutFlow()
                  } catch (error) {
                    setError(error)
                  }
                }
              },
              {
                icon: <FontAwesome6 name="trash" size={20} color={danger}/>,
                text: "Delete Account",
                onPress: () => {
                  Analytics.track('delete_account_pressed')
                  setDeletingAccount(true)
                },
                color: danger,
                warningHaptic: true
              }
            ]}
          />
        </View>
      </View>

      <View style={{width: "100%", paddingHorizontal: 16, gap: 16}}>
        {(user as User & {isSuperUser: boolean})?.isSuperUser && (
          <AdminDropdown />
        )}
        <View style={{flexDirection: "row", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap"}}>
          <Text style={[textStyles.subscript, {color: theme.colors.textSecond}]}>
            v{version}
          </Text>
          <Text style={[textStyles.subscript, {color: theme.colors.textSecond}]}>
            {deviceId}
          </Text>
        </View>
      </View>
    </ScrollView>
    <ThemedDialog
      visible={error}
      errorAnimation={error}
      title="Error"
      subMessage={error instanceof Error ? error.message : 'Unknown error occurred.'}
      options={[
        {
          title: 'Okay',
          onPress: () => setError(null),
          role: 'destructive',
          icon: <Ionicons name="checkmark" size={22}/>
        }
      ]}
    />
    <ThemedDialog
      visible={deletingAccount}
      title="Delete account?"
      subMessage="You are about to start closing your Impact Banking account. On the next screen you can share feedback and confirm deletion. Are you sure you wish to continue?"
      options={[
        {
          title: 'Continue',
          onPress: handleConfirmDeleteAccount,
          role: 'destructive',
          icon: <Ionicons name="checkmark" size={22}/>
        },
        {
          title: 'Cancel',
          onPress: () => {
            Analytics.track('delete_account_cancelled')
            setDeletingAccount(false)
          },
          role: 'cancel',
          icon: <Ionicons name="close" size={22}/>
        }
      ]}
    />
    <ThemedDialog
      visible={passwordResetState === PasswordResetState.CONFIRMING}
      title="Confirm Password Reset"
      subMessage="Are you sure you want to reset your password? If so, you will receive an email with instructions to reset your password."
      options={[
        {
          title: 'Yes',
          onPress: handleTriggerPasswordReset,
          role: 'confirm',
          icon: <Ionicons name="checkmark" size={22}/>
        },
        {
          title: 'No',
          onPress: () => setPasswordResetState(PasswordResetState.IDLE),
          role: 'cancel',
          icon: <Ionicons name="close" size={22}/>
        }
      ]}
    />
    <ThemedDialog
      visible={passwordResetState === PasswordResetState.SENT}
      title="Password Reset Email Sent"
      subMessage="A password reset email has been sent to your email address. Please check your inbox and follow the instructions to reset your password."
      options={[
        {
          title: 'Okay',
          onPress: () => setPasswordResetState(PasswordResetState.CONFIRMED),
          role: 'confirm',
          icon: <Ionicons name="checkmark" size={22}/>
        }
      ]}
    />
    <CustomActionSheet
      ref={specifyValuesSheetRef}
      name="settings-specify-values"
    >
      <SpecifyValues
        hideSheet={() => specifyValuesSheetRef.current?.hide()}
      />
    </CustomActionSheet>
    <CustomActionSheet
      ref={feedbackSheetRef}
      name="settings-feedback-form"
    >
      <AppFeedbackForm hideSheet={() => feedbackSheetRef.current?.hide()} />
    </CustomActionSheet>
  </>
}

type SettingsSectionProps = {
  title: string;
  data: {
    icon: React.ReactNode;
    onPress?: () => void;
    screen?: string;
    text: string;
    component?: React.ReactNode;
    color?: string;
    disabled?: boolean;
    warningHaptic?: boolean;
  }[]
}

const SettingsSection = ({ title, data }: SettingsSectionProps) => {
  const { theme } = useContext(ThemeContext)
  const { textMain, bgSecond, borderColor } = theme.colors

  const navigation = useNavigation()

  return (
    <View style={{ gap: 10 }}>
      <Text style={[textStyles.h2, { color: textMain }]}>{title}</Text>
      <View style={{ gap: 5 }}>
        {data.map((item, index) => {
          const content = (
            <ShadowView
              style={[
                styles.settingRow,
                {
                  borderColor,
                  backgroundColor: bgSecond,
                  opacity: item.disabled ? 0.5 : 1,
                }
              ]}
            >
              <View style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 5 }}>
                <View style={{ width: 30, alignItems: "center", justifyContent: "center" }}>
                  {item.icon}
                </View>
                <Text style={[textStyles.body, { color: item.color || textMain }]}>
                  {item.text}
                </Text>
              </View>
              {item.component}
            </ShadowView>
          )

          if (item.screen) {
            return (
              <TouchableOpacity
                key={index}
                onPress={() => {
                  Haptics.selection()
                  navigation.dispatch(StackActions.push(item.screen))
                }}
              >
                {content}
              </TouchableOpacity>
            )
          }

          // Otherwise fallback to a regular onPress
          return (
            <TouchableOpacity
              key={index}
              onPress={() => {
                if (item.warningHaptic) {
                  Haptics.notificationWarning()
                } else {
                  Haptics.selection()
                }
                item.onPress()
              }}
              disabled={item.disabled || !item.onPress}
            >
              {content}
            </TouchableOpacity>
          )
        })}
      </View>
    </View>
  )
}

const AdminDropdown = () => {
  const { theme } = useContext(ThemeContext)
  const { textMain, bgSecond, borderColor, textSecond } = theme.colors

  const [open, setOpen] = useState(false)
  const [error, setError] = useState(null)

  const animated = React.useRef(new Animated.Value(0)).current
  const [measuredHeight, setMeasuredHeight] = useState(0)

  const dispatch = useAppDispatch()
  const { user, deviceId } = useAppSelector((state) => state.auth)

  const safeAreaInsets = useSafeAreaInsets()

  const expoPushToken =
    Platform.OS === 'web' ? undefined : user?.appPermissions?.[deviceId]?.notifications?.pushToken

  const toggle = () => {
    Haptics.selection()
    Animated.timing(animated, {
      toValue: open ? 0 : 1,
      duration: 200,
      useNativeDriver: false,
    }).start()
    setOpen((prev) => !prev)
  }

  const height = animated.interpolate({
    inputRange: [0, 1],
    outputRange: [0, measuredHeight || 1],
  })
  const arrowAngle = animated.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '90deg'],
  })

  useEffect(() => {
    if (error) {
      throw new Error(error)
    }
  }, [error])

  const sendSentryTestReport = () => {
    Sentry.captureMessage('Admin Sentry test message (no crash)', 'info')
    Alert.alert('Sent', 'A non-crashing test message was sent to Sentry.')
  }

  const triggerErrorBoundaryTest = () => {
    setError('Admin Sentry ErrorBoundary test')
  }

  const triggerHardCrash = () => {
    Alert.alert(
      'Force app crash?',
      'This intentionally crashes the app after sending a Sentry exception. Use only when validating crash capture.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Crash App',
          style: 'destructive',
          onPress: () => {
            Sentry.captureException(new Error('Admin Sentry hard-crash test'))
            // Defer throw so it escapes React handlers and behaves like a fatal crash path.
            setTimeout(() => {
              throw new Error('Admin intentional hard crash')
            }, 0)
          }
        }
      ]
    )
  }

  return (
    <View style={{ width: '100%' }}>
      <TouchableOpacity onPress={toggle} activeOpacity={0.8}>
        <ShadowView
          style={{
            width: '100%',
            paddingVertical: 10,
            paddingHorizontal: 16,
            borderRadius: 10,
            borderWidth: 1,
            borderColor,
            backgroundColor: bgSecond,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Text style={[textStyles.h2, { color: textMain }]}>Admin Menu</Text>
          <Animated.View style={{ transform: [{ rotate: arrowAngle }] }}>
            <Ionicons name='chevron-forward' size={20} color={textSecond} />
          </Animated.View>
        </ShadowView>
      </TouchableOpacity>
      <Animated.View style={{ width: '100%', overflow: 'hidden', height }}>
        <View
          style={{ width: '100%', gap: 10, paddingVertical: 16 }}
          onLayout={(e) => setMeasuredHeight(e.nativeEvent.layout.height)}
        >
          {/* User States */}
          <View style={{ gap: 10 }}>
            <Text style={[textStyles.h2, { color: textMain }]}>Test User States</Text>
            <ThemedButton
              text="Go to Onboarding"
              onPress={() => {
                dispatch(setUserField({ field: 'hasOnboarded', value: false }))
              }}
              variant="primary"
            />
            
          </View>
          {/* Haptics */}
          <View style={{ gap: 10 }}>
            <Text style={[textStyles.h2, { color: textMain }]}>Haptics</Text>
            {[
              { key: 'Light', action: () => Haptics.impactLight() },
              { key: 'Medium', action: () => Haptics.impactMedium() },
              { key: 'Heavy', action: () => Haptics.impactHeavy() },
              { key: 'Rigid', action: () => Haptics.impactRigid() },
              { key: 'Soft', action: () => Haptics.impactSoft() },
            ].map(({ key, action }) => (
              <ThemedButton key={key} text={key} onPress={action} variant="primary" hapticLevel="none" />
            ))}
            <ThemedButton text="Notification Success" onPress={() => Haptics.notificationSuccess()} variant="primary" hapticLevel="none" />
            <ThemedButton text="Notification Warning" onPress={() => Haptics.notificationWarning()} variant="primary" hapticLevel="none" />
            <ThemedButton text="Notification Error" onPress={() => Haptics.notificationError()} variant="primary" hapticLevel="none" />
            <ThemedButton text="Selection" onPress={() => Haptics.selection()} variant="primary" hapticLevel="none" />
          </View>

          {/* Notifications Debug */}
          {Platform.OS !== 'web' ? (
            <View style={{ gap: 10 }}>
              <Text style={[textStyles.h2, { color: textMain }]}>Notifications</Text>
              <Text style={{ padding: 10, backgroundColor: '#eee', width: '100%' }} selectable>
                {expoPushToken || 'No Expo push token found for this device.'}
              </Text>
              <ThemedButton
                text="Copy Expo Push Token"
                onPress={async () => {
                  try {
                    if (!expoPushToken) {
                      Alert.alert('No token', 'Enable notifications first to generate a push token.')
                      return
                    }
                    const success = await Clipboard.setStringAsync(expoPushToken)
                    if (success) {
                      Haptics.notificationSuccess()
                      Alert.alert('Copied', 'Expo push token copied to clipboard.')
                    } else {
                      Haptics.notificationError()
                      Alert.alert('Copy failed', 'Unable to copy token to clipboard.')
                    }
                  } catch (e: any) {
                    Haptics.notificationError()
                    Alert.alert('Copy failed', e?.message || 'Unable to copy token to clipboard.')
                  }
                }}
                variant="primary"
                hapticLevel="none"
                disabled={!expoPushToken}
              />
            </View>
          ) : null}

          {/* Layout Debug */}
          <View style={{ gap: 10 }}>
            <Text style={[textStyles.h2, { color: textMain }]}>Layout</Text>
            <Text style={{ padding: 10, backgroundColor: '#eee', width: '100%' }}>
              {JSON.stringify(safeAreaInsets, null, 2)}
            </Text>
          </View>

          {/* Error Reporting */}
          <View style={{ gap: 10 }}>
            <Text style={[textStyles.h2, { color: textMain }]}>Error Reporting</Text>
            <ThemedButton
              text="Send Sentry Test Report (No Crash)"
              onPress={sendSentryTestReport}
              variant="primary"
            />
            <ThemedButton
              text="Trigger ErrorBoundary Screen"
              onPress={triggerErrorBoundaryTest}
              variant="primary"
            />
            <ThemedButton
              text="Force App Crash (Fatal Test)"
              onPress={triggerHardCrash}
              variant="primary"
            />
          </View>

          {/* Expo Config */}
          {Platform.OS !== 'web' ? (
          <View style={{ gap: 10 }}>
            <Text style={[textStyles.h2, { color: textMain }]}>Expo Config</Text>
            <Text style={{ padding: 10, backgroundColor: '#eee', width: '100%' }}>
              {JSON.stringify(ExpoApplication, null, 2)}
            </Text>
          </View>
          ) : null}

          {/* App Config */}
          <View style={{ gap: 10 }}>
            <Text style={[textStyles.h2, { color: textMain }]}>App Config</Text>
            <Text style={{ padding: 10, backgroundColor: '#eee', width: '100%' }}>
              {JSON.stringify(Constants.expoConfig, null, 2)}
            </Text>
          </View>
        </View>
      </Animated.View>
    </View>
  )
}

const styles = StyleSheet.create({
  settingRow: {
    width: "100%",
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },
})

export default Settings
